'use client'

import { useState, ChangeEvent } from 'react'
import { Divider, Cell, Checkbox, InputText, Card, Text } from '@vinted/web-ui'

import { businessAccountFromUserDto } from '@marketplace-web/domain/business-accounts-data'
import { useSession } from '@marketplace-web/shared/session'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { SeparatedList } from '@marketplace-web/shared/ui-helpers'

import { useProfileSettingsContext } from '@marketplace-web/domain/profile-settings'

enum Field {
  LegalName = 'businessDetails.legalName',
  LegalCode = 'businessDetails.legalCode',
  EntityType = 'businessDetails.entityType',
  Vat = 'businessDetails.vat',
  IsVatDisabled = 'businessDetails.isVatDisabled',
}

const BusinessDetails = () => {
  const translate = useTranslate('profile_settings.business_details')
  const {
    setSettings,
    settings: { vat },
  } = useProfileSettingsContext()

  const { user } = useSession()
  const businessAccount = businessAccountFromUserDto(user)

  const [isVatDisabled, setIsVatDisabled] = useState(!businessAccount?.vat)

  function handleVatChange(event: ChangeEvent<HTMLInputElement>) {
    setSettings(prevSettings => ({ ...prevSettings, vat: event.target.value }))
  }

  function handleIsVatDisabledChange() {
    setIsVatDisabled(prevIsVatDisabled => {
      if (!prevIsVatDisabled) {
        setSettings(prevSettings => ({ ...prevSettings, vat: null }))
      }

      return !prevIsVatDisabled
    })
  }

  if (!businessAccount) return null

  return (
    <Card>
      <Cell styling={Cell.Styling.Wide}>
        <Text
          as="h4"
          width={Text.Width.Parent}
          alignment={Text.Alignment.Left}
          type={Text.Type.Subtitle}
          text={translate('title')}
        />
      </Cell>
      <Cell styling={Cell.Styling.Tight}>
        <SeparatedList separator={<Divider />}>
          <Cell styling={Cell.Styling.Tight}>
            <InputText
              name={Field.LegalName}
              title={translate('fields.legal_name.title')}
              styling={InputText.Styling.Wide}
              value={businessAccount.legal_name ?? ''}
              disabled
              onChange={() => undefined}
            />
          </Cell>
          <Cell styling={Cell.Styling.Tight}>
            <InputText
              name={Field.LegalCode}
              title={translate('fields.legal_code.title')}
              styling={InputText.Styling.Wide}
              value={businessAccount.legal_code ?? ''}
              disabled
              onChange={() => undefined}
            />
          </Cell>
          <Cell styling={Cell.Styling.Tight}>
            <InputText
              name={Field.EntityType}
              title={translate('fields.entity_type.title')}
              styling={InputText.Styling.Wide}
              value={businessAccount.entity_type_title ?? ''}
              disabled
              onChange={() => undefined}
            />
          </Cell>
          <>
            <Cell styling={Cell.Styling.Tight}>
              <InputText
                name={Field.Vat}
                styling={InputText.Styling.Wide}
                title={translate('fields.vat.title')}
                placeholder={translate('fields.vat.placeholder')}
                onChange={handleVatChange}
                value={vat ?? ''}
                disabled={isVatDisabled}
              />
            </Cell>
            <Cell
              styling={Cell.Styling.Wide}
              title={<label htmlFor={Field.IsVatDisabled}>{translate('fields.check.title')}</label>}
              suffix={
                <Checkbox
                  name={Field.IsVatDisabled}
                  checked={isVatDisabled}
                  onChange={handleIsVatDisabledChange}
                />
              }
            />
          </>
        </SeparatedList>
      </Cell>
    </Card>
  )
}

export default BusinessDetails
